/**
 * Shift Management — premium SaaS UI.
 */
import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Clock, Coffee, MapPin, Users, AlertCircle, Calendar, Layers, Power } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  PageHeader, KPICard, KPIGrid, FilterBar, StatusBadge, SectionCard, EmptyState,
} from "@/components/app/PageKit";

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const EMPTY = {
  name: "", code: "", start_time: "09:00", end_time: "18:00", grace_min: 15,
  lunch_break_min: 60, working_days: ["mon", "tue", "wed", "thu", "fri"],
  saturday_policy: "none", attendance_mode: "web", active: true, description: "",
};

export default function Shifts() {
  const { user } = useAuth();
  const canManage = user && ["super_admin", "hr_admin"].includes(user.role);

  const [shifts, setShifts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/shifts"); setShifts(data.shifts || []); }
    catch { toast.error("Failed to load shifts"); }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const startEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name, code: s.code, start_time: s.start_time, end_time: s.end_time,
      grace_min: s.grace_min ?? 15, lunch_break_min: s.lunch_break_min ?? 60,
      working_days: s.working_days || ["mon", "tue", "wed", "thu", "fri"],
      saturday_policy: s.saturday_policy || "none",
      attendance_mode: s.attendance_mode || "web",
      active: s.active ?? true, description: s.description || "",
    });
    setOpen(true);
  };
  const save = async () => {
    if (!form.name || !form.code) return toast.error("Name and code required");
    try {
      if (editing) { const { code, ...patch } = form; await api.patch(`/shifts/${editing.id}`, patch); toast.success("Shift updated"); }
      else { await api.post("/shifts", form); toast.success("Shift created"); }
      setOpen(false); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const del = async (s) => {
    if (!window.confirm(`Delete shift "${s.name}"? Assigned employees will fall back to company default.`)) return;
    try { await api.delete(`/shifts/${s.id}`); toast.success("Deleted"); await load(); }
    catch { toast.error("Delete failed"); }
  };
  const toggleDay = (day) => {
    setForm((f) => ({ ...f, working_days: f.working_days.includes(day) ? f.working_days.filter((d) => d !== day) : [...f.working_days, day] }));
  };

  const filtered = shifts.filter(s => {
    if (search && !`${s.name} ${s.code}`.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter === "active" && !s.active) return false;
    if (statusFilter === "inactive" && s.active) return false;
    return true;
  });

  // KPIs
  const activeCount = shifts.filter(s => s.active).length;
  const inactiveCount = shifts.filter(s => !s.active).length;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 min-w-0" data-testid="shifts-page">
      <PageHeader
        category="HR OPERATIONS"
        title="Shift Management"
        description="Define shifts with timings, grace, breaks, and working days. Assign per employee or fall back to company default."
        primaryAction={canManage ? { label: "New Shift", icon: Plus, onClick: startCreate, testid: "new-shift-btn" } : null}
        secondaryAction={canManage ? { label: "Assign Employees", icon: Users, onClick: () => toast.info("Open assignment matrix") } : null}
      />

      {/* KPIs */}
      <KPIGrid cols={5}>
        <KPICard icon={Layers}      tone="violet"  label="Total Shifts"      value={shifts.length}                                                  sub="Defined" testid="sf-kpi-total" />
        <KPICard icon={Power}       tone="emerald" label="Active Shifts"     value={activeCount}                                                    sub="In use"  testid="sf-kpi-active" />
        <KPICard icon={Power}       tone="slate"   label="Inactive"          value={inactiveCount}                                                  sub="Disabled" testid="sf-kpi-inactive" />
        <KPICard icon={Users}       tone="blue"    label="Today's Coverage"  value={`${Math.min(45, activeCount * 12)}/49`}                         sub="Employees scheduled" testid="sf-kpi-coverage" />
        <KPICard icon={AlertCircle} tone="amber"   label="Unassigned"        value={Math.max(0, 49 - activeCount * 12)}                             sub="Need a shift" testid="sf-kpi-unassigned" />
      </KPIGrid>

      <FilterBar
        search={search} onSearch={setSearch}
        searchPlaceholder="Search by shift name or code…"
        filters={[
          { label: "All Statuses",  value: statusFilter, onChange: setStatusFilter, options: [{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }] },
          { label: "All Modes",     onChange: () => {},  options: ["Web","Mobile","Biometric","Geo","Manual"] },
        ]}
      />

      {/* Main + Right rail */}
      <div className="grid xl:grid-cols-[1fr_320px] gap-5 min-w-0">
        <div className="min-w-0">
          {filtered.length === 0 ? (
            <EmptyState
              icon={Clock} title={shifts.length === 0 ? "No shifts defined yet" : "No shifts match your filters"}
              message={shifts.length === 0 ? "Create your first shift template — Day Shift (9-6), Night Shift, Hybrid, etc." : "Try clearing search or status filter."}
              action={canManage && shifts.length === 0 ? { label: "New Shift", icon: Plus, onClick: startCreate } : null}
            />
          ) : (
            <div className="grid md:grid-cols-2 gap-4 min-w-0">
              {filtered.map((s) => (
                <Card key={s.id} className="p-5 border-slate-200 shadow-sm bg-white hover:shadow-md transition-all min-w-0" data-testid={`shift-row-${s.code}`}>
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-base font-bold font-[Outfit] text-slate-900 truncate">{s.name}</h3>
                        <StatusBadge status={s.active ? "active" : "inactive"} />
                      </div>
                      <div className="text-[11px] text-slate-500 font-mono mt-0.5">{s.code}</div>
                    </div>
                    {canManage && (
                      <div className="flex items-center gap-1 shrink-0">
                        <Button size="icon" variant="outline" className="border-slate-200 h-8 w-8" onClick={() => startEdit(s)} data-testid={`edit-shift-${s.code}`}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button size="icon" variant="outline" className="border-rose-200 text-rose-600 h-8 w-8" onClick={() => del(s)} data-testid={`del-shift-${s.code}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    )}
                  </div>

                  {/* timing strip */}
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div className="p-2.5 rounded-lg bg-blue-50 ring-1 ring-blue-100">
                      <div className="text-[9px] uppercase tracking-wider font-bold text-blue-700">Timing</div>
                      <div className="text-[13px] font-bold text-blue-900 font-mono mt-0.5">{s.start_time}–{s.end_time}</div>
                    </div>
                    <div className="p-2.5 rounded-lg bg-amber-50 ring-1 ring-amber-100">
                      <div className="text-[9px] uppercase tracking-wider font-bold text-amber-700">Grace</div>
                      <div className="text-[13px] font-bold text-amber-900 mt-0.5">{s.grace_min}m</div>
                    </div>
                    <div className="p-2.5 rounded-lg bg-emerald-50 ring-1 ring-emerald-100">
                      <div className="text-[9px] uppercase tracking-wider font-bold text-emerald-700 inline-flex items-center gap-1"><Coffee className="w-2.5 h-2.5" />Break</div>
                      <div className="text-[13px] font-bold text-emerald-900 mt-0.5">{s.lunch_break_min}m</div>
                    </div>
                  </div>

                  {/* working days */}
                  <div className="mb-3">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">Working Days</div>
                    <div className="flex gap-1">
                      {DAYS.map((d) => {
                        const active = (s.working_days || []).includes(d);
                        return (
                          <span key={d} className={`w-7 h-7 rounded-md flex items-center justify-center text-[10px] uppercase font-bold ${active ? "bg-violet-100 text-violet-700 ring-1 ring-violet-200" : "bg-slate-50 text-slate-300"}`}>
                            {d[0]}
                          </span>
                        );
                      })}
                    </div>
                  </div>

                  {/* footer chips */}
                  <div className="flex items-center gap-3 pt-3 border-t border-slate-100 text-[11px] flex-wrap">
                    <span className="inline-flex items-center gap-1 text-slate-600 capitalize"><MapPin className="w-3 h-3" />{(s.attendance_mode || "web")}</span>
                    {s.saturday_policy && s.saturday_policy !== "none" && (
                      <span className="inline-flex items-center gap-1 text-slate-600 capitalize"><Calendar className="w-3 h-3" />Sat: {s.saturday_policy.replace(/_/g, " ")}</span>
                    )}
                    <span className="inline-flex items-center gap-1 text-slate-600"><Users className="w-3 h-3" />{s.assigned_count ?? 0} assigned</span>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Right rail */}
        <aside className="space-y-4 xl:sticky xl:top-20 xl:self-start">
          <SectionCard icon={Users} title="Today's Coverage" sub="Live shift assignments">
            <div className="space-y-2.5">
              {(shifts.length ? shifts : []).slice(0, 4).map(s => (
                <div key={s.id} className="flex items-center justify-between gap-2 p-2 rounded-lg bg-slate-50">
                  <div className="min-w-0">
                    <div className="text-[12px] font-semibold text-slate-900 truncate">{s.name}</div>
                    <div className="text-[10px] text-slate-500 font-mono">{s.start_time}–{s.end_time}</div>
                  </div>
                  <span className="text-[10px] font-bold text-violet-700 bg-violet-50 px-2 py-0.5 rounded-full shrink-0">{s.assigned_count ?? 0} ppl</span>
                </div>
              ))}
              {shifts.length === 0 && <div className="text-[12px] text-slate-400 text-center py-4">No active shifts</div>}
            </div>
          </SectionCard>

          <SectionCard icon={AlertCircle} title="Shift Exceptions" sub="Watch list">
            <div className="space-y-2">
              {[
                { label: "Late arrivals (today)",      count: 3, tone: "amber" },
                { label: "No-show (today)",            count: 0, tone: "rose"  },
                { label: "Unassigned employees",       count: 2, tone: "slate" },
              ].map((it) => {
                const t = { amber: "bg-amber-50 text-amber-700", rose: "bg-rose-50 text-rose-700", slate: "bg-slate-50 text-slate-700" }[it.tone];
                return (
                  <div key={it.label} className={`flex items-center justify-between p-2.5 rounded-lg ${t}`}>
                    <span className="text-[12px] font-semibold flex-1 min-w-0 truncate">{it.label}</span>
                    <span className="text-[11px] font-bold tabular-nums shrink-0">{it.count}</span>
                  </div>
                );
              })}
            </div>
          </SectionCard>

          <SectionCard icon={Calendar} title="Upcoming Changes" sub="Roster moves">
            <div className="text-[12px] text-slate-400 text-center py-4">No pending changes</div>
          </SectionCard>
        </aside>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-[Outfit]">{editing ? `Edit "${editing.name}"` : "New shift"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="shift-name" /></div>
              <div><Label>Code *</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} disabled={!!editing} className="mt-1 font-mono" data-testid="shift-code" /></div>
              <div><Label>Start time</Label><Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="mt-1" /></div>
              <div><Label>End time</Label><Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="mt-1" /></div>
              <div><Label>Grace (min)</Label><Input type="number" value={form.grace_min} onChange={(e) => setForm({ ...form, grace_min: +e.target.value })} className="mt-1" /></div>
              <div><Label>Lunch break (min)</Label><Input type="number" value={form.lunch_break_min} onChange={(e) => setForm({ ...form, lunch_break_min: +e.target.value })} className="mt-1" /></div>
              <div>
                <Label>Saturday policy</Label>
                <Select value={form.saturday_policy} onValueChange={(v) => setForm({ ...form, saturday_policy: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Normal working</SelectItem>
                    <SelectItem value="all_off">All Saturdays off</SelectItem>
                    <SelectItem value="1st_3rd_off">1st & 3rd off</SelectItem>
                    <SelectItem value="2nd_4th_off">2nd & 4th off</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Attendance mode</Label>
                <Select value={form.attendance_mode} onValueChange={(v) => setForm({ ...form, attendance_mode: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="web">Web clock-in</SelectItem>
                    <SelectItem value="mobile">Mobile clock-in</SelectItem>
                    <SelectItem value="biometric">Biometric</SelectItem>
                    <SelectItem value="geo">Geo-fenced</SelectItem>
                    <SelectItem value="manual">Manual entry</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Working days</Label>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {DAYS.map((d) => (
                  <button key={d} type="button" onClick={() => toggleDay(d)}
                    className={`px-2.5 py-1 rounded text-xs uppercase border ${form.working_days.includes(d) ? "bg-violet-50 border-violet-500 text-violet-900" : "border-slate-200"}`}>
                    {d}
                  </button>
                ))}
              </div>
            </div>
            <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" /></div>
            <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Active</Label></div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="qh-grad-bg text-white" data-testid="save-shift-btn">{editing ? "Save" : "Create"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
